import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';

const LegalIntelligenceAnalyticsSection = () => {
  const { t, isRTL } = useLanguage();
  const [data, setData] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setUnavailable(false);
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_LEGAL_INTELLIGENCE_ANALYTICS), {
          credentials: 'include',
        });
        if (!res.ok) {
          if (!cancel) {
            setUnavailable(true);
            setData(null);
          }
          return;
        }
        const json = await res.json();
        if (cancel) return;
        if (!json || json.available === false) {
          setUnavailable(true);
          setData(null);
          return;
        }
        setData(json);
      } catch {
        if (!cancel) {
          setUnavailable(true);
          setData(null);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="admin-analytics-block admin-analytics-card-skin admin-analytics--legal" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="admin-state admin-state--loading" aria-live="polite">
          <div className="admin-loading-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
          <p className="admin-state__message">{t('adminAnalytics.loading')}</p>
        </div>
      </div>
    );
  }

  if (unavailable || !data) {
    return (
      <div className="admin-analytics-block admin-analytics-card-skin admin-analytics--legal" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="admin-analytics-hero">
          <h2 className="admin-analytics-title" id="admin-legal-intelligence-analytics-title">
            {t('adminAnalytics.legalIntelligenceTitle')}
          </h2>
        </header>
        <div className="admin-state admin-state--unavailable" role="status">
          <p className="admin-state__message">{t('adminAnalytics.legalIntelligenceUnavailable')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-analytics admin-analytics-block admin-analytics--legal" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="admin-analytics-hero">
        <h2 className="admin-analytics-title" id="admin-legal-intelligence-analytics-title">
          {t('adminAnalytics.legalIntelligenceTitle')}
        </h2>
        <p className="admin-analytics-subtitle">{t('adminAnalytics.legalIntelligenceBlurb')}</p>
      </header>
      <div className="admin-summary admin-summary--metrics">
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.legalRegisteredUsers')}</div>
          <div className="summary-value">{data.registeredUsers ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.legalGuests')}</div>
          <div className="summary-value">{data.guests ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.legalAnalyses')}</div>
          <div className="summary-value">{data.analyses ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('adminAnalytics.legalVisitors')}</div>
          <div className="summary-value">{data.visitors ?? 0}</div>
        </div>
      </div>
      {Array.isArray(data.analysesByDay) && data.analysesByDay.length > 0 ? (
        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('adminAnalytics.legalRecentActivity')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('adminAnalytics.legalDay')}</th>
                  <th>{t('adminAnalytics.legalCount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.analysesByDay.slice(0, 7).map((row) => (
                  <tr key={row.day}>
                    <td>{row.day}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default LegalIntelligenceAnalyticsSection;
